import type { SelectionLadder } from "@/dashboard/inventory_management/common/selectionLadder";
import type { UsePortfolioSelectionOptions } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import type {
  CnmcHoldingInfo,
  CnmcRoute,
} from "@/dashboard/production_strategy/issuance-reporting/es/cnmcTypes";
import type { FlattenedPortfolioItem } from "@/types";
import { apiErrorResponse } from "@/utils/apiErrors";
import {
  type RegistrationAuthority,
  registrationAuthorityForRegion,
} from "@/utils/registryAccounts";
import { sumBy } from "lodash-es";

/**
 * A row's CNMC annotation (`useCnmcHoldings`); undefined for a row that is not a live CNMC holding,
 * including every row of another registry.
 */
export type CnmcHoldingLookup = (
  item: FlattenedPortfolioItem
) => CnmcHoldingInfo | undefined;

/** Whether the row is in the Spanish (CNMC) registry, so only a CNMC filing can send it. */
export const isCnmcRegistryRow = (item: FlattenedPortfolioItem): boolean =>
  item.registry_region === "ES";

/** A CNMC holding of a connection that may not prepare transfers and exports yet. */
export const isOutboundDisabled = (
  info: CnmcHoldingInfo | undefined
): boolean => info !== undefined && !info.outbound_enabled;

/**
 * What one send may contain: registry-API GOs of one registration authority, or the CNMC GOs of one
 * connection and one route. Two items can share a send only when their kinds are equal; a transfer
 * always executes inside one authority's registry, there is no inter-authority transfer. `CNMC:NONE`
 * is a Spanish row without an annotation, which no send can take.
 */
export type SendKind =
  | `REGISTRY:${RegistrationAuthority}`
  | `CNMC:${number}:${CnmcRoute}`
  | "CNMC:NONE";

export const sendKind = (
  item: FlattenedPortfolioItem,
  info: CnmcHoldingInfo | undefined
): SendKind => {
  if (info) return `CNMC:${info.connection_id}:${info.route}`;
  if (isCnmcRegistryRow(item)) return "CNMC:NONE";
  return `REGISTRY:${registrationAuthorityForRegion(item.registry_region)}`;
};

/** The registration authority a registry-API send executes in; null for a CNMC send and no send. */
export const sendKindAuthority = (
  kind: SendKind | null
): RegistrationAuthority | null => {
  const [scheme, authority] = kind?.split(":") ?? [];
  return scheme === "REGISTRY" ? (authority as RegistrationAuthority) : null;
};

/** The connection and route a CNMC send files; null for a registry-API send, `CNMC:NONE` and no send. */
export const cnmcSendTarget = (
  kind: SendKind | null
): { connectionId: number; route: CnmcRoute } | null => {
  const [scheme, connectionId, route] = kind?.split(":") ?? [];
  return scheme === "CNMC" && route !== undefined
    ? { connectionId: Number(connectionId), route: route as CnmcRoute }
    : null;
};

/**
 * Why `item`, of another send kind, cannot join a send that holds `selected`, as an i18n key.
 * Rows without an annotation never get here: the selection disables them first.
 */
export const sendKindConflictReason =
  (lookup: CnmcHoldingLookup) =>
  (selected: FlattenedPortfolioItem, item: FlattenedPortfolioItem): string => {
    const selectedInfo = lookup(selected);
    const info = lookup(item);
    if (!selectedInfo && !info) return "go_send.mixed_registry_selection_error";
    if (!selectedInfo || !info)
      return "cnmc.outbound.kind_reasons.separate_registries";
    if (selectedInfo.connection_id !== info.connection_id)
      return "cnmc.outbound.kind_reasons.one_account";
    return "cnmc.outbound.kind_reasons.separate_routes";
  };

/** Whether any of the rows is a CNMC holding that cannot be sent yet. */
export const hasDisabledHoldings = (
  items: readonly FlattenedPortfolioItem[],
  lookup: CnmcHoldingLookup
): boolean => items.some((item) => isOutboundDisabled(lookup(item)));

/**
 * MWh of the annotated rows that issuance-plan deliveries are promised, so Send cannot take them.
 * Committed (pending) rows carry no annotation, so passing every row is safe.
 */
export const promisedMwh = (
  items: readonly FlattenedPortfolioItem[],
  lookup: CnmcHoldingLookup
): number =>
  sumBy(items, (item) => {
    const info = lookup(item);
    return info ? Math.max(0, item.count - info.available_count) : 0;
  });

/** The `error_type` of a refused outbound creation (409) that a fresh preview explains, with its notice. */
const REPREVIEW_NOTICE_KEYS: Record<string, string> = {
  CNMC_OUTBOUND_AVAILABILITY_CHANGED: "cnmc.outbound.availability_changed",
  CNMC_OUTBOUND_BLOCKED: "cnmc.outbound.availability_changed",
  CNMC_FILING_PROFILE_REQUIRED: "cnmc.outbound.profile_missing",
};

/**
 * The i18n key of the notice to show above a re-preview when creating a filing was refused because
 * the selection's availability or the filing details changed; null for any other failure.
 */
export const outboundRepreviewNoticeKey = (error: unknown): string | null => {
  const response = apiErrorResponse(error);
  const errorType = response?.data?.error_type;
  return response?.status === 409 && typeof errorType === "string"
    ? (REPREVIEW_NOTICE_KEYS[errorType] ?? null)
    : null;
};

/**
 * Keeps every Send selection an oldest-first prefix of each CNMC ladder, in the order the server
 * says CNMC debits it. Rows without an annotation sit outside any ladder.
 */
export const cnmcLadder = (lookup: CnmcHoldingLookup): SelectionLadder => ({
  groupKey: (item) => lookup(item)?.ladder_key ?? null,
  rank: (item) => lookup(item)?.ladder_rank ?? "",
});

/**
 * The rules of a Send selection, reading each row's CNMC annotation through `lookup`: one send kind
 * per send, each CNMC ladder taken oldest first, never a holding of a connection without transfers
 * and exports nor a Spanish row that is no live holding, and of each holding only what no
 * issuance-plan delivery is promised.
 */
export const cnmcSendRules = (lookup: CnmcHoldingLookup) =>
  ({
    disabledReason: (item) => {
      const info = lookup(item);
      if (isOutboundDisabled(info)) return "cnmc.outbound.not_enabled";
      if (!info && isCnmcRegistryRow(item))
        return "cnmc.outbound.not_a_live_holding";
      return null;
    },
    exclusive: {
      key: (item) => sendKind(item, lookup(item)),
      conflictReason: sendKindConflictReason(lookup),
    },
    ladder: cnmcLadder(lookup),
    maxCount: (item) => lookup(item)?.available_count ?? item.count,
  }) satisfies UsePortfolioSelectionOptions<SendKind>;
